<template>
  <div class="community-hero" :class="{'collapsed': collapseHero}">
    <div class="tw-container">
      <div class="community-hero__back-button-wrapper tw-flex items-center">
        <ds-button
          class="second-top-bar__link-item"
          :label="collapseHero ? '' : $t('communities_back')"
          icon="arrow-left"
          variant="minimal"
          to="/communities"
          :style="collapseHero ? 'color: white; fill: white;' : 'margin-top: 15px; color: white; fill: white;'"
          v-tooltip="$t('communities_back')"
          icon-fill-color="#ffffff"
        />
        <div class="community-hero__collapsed-info" v-if="collapseHero">
          <h4 class="community-hero__collapsed-community-title">
            {{ community.name }}
          </h4>
          <div class="community-hero__collapsed-description-row" v-if="community.description">
            <span class="community-hero__collapsed-description" v-html="truncatedDescription">
            </span>
            <span
              v-if="isSubsection && community.description && community.description.length > 80"
              class="community-hero__see-more"
              @click.stop="$emit('expand')"
            >
              {{ $t('knowledge_sharing_read_more') }}
            </span>
          </div>
        </div>
      </div>

      <BasicHero
        class="!mt-0 !pt-2"
        show-expand-button
        :title="community.name"
        :subtitle="showSubTitle ? community.description : ''"
        :force-read-more="showCollapseButton && !collapseHero"
        :hide-read-more-button="isSubsection"
      >
        <div
          v-if="showCollapseButton && !collapseHero"
          class="community-hero__show-less-container"
        >
          <span
            class="community-hero__show-less"
            @click.stop="$emit('collapse')"
          >
            {{ $t('knowledge_sharing_read_less') }}
          </span>
        </div>
        <slot v-if="$slots.navigation" name="navigation">
        </slot>
      </BasicHero>
    </div>
  </div>
</template>

<script>
  import BasicHero from '../BasicHero/BasicHero.vue'
  import TranslationsMixin from '../../util/TranslationsMixin'

  export default {
    props: {
      community: {},
      showSubTitle: {
        default: true,
        type: Boolean,
      },
      collapseHero: {
        type: Boolean,
        default: false,
      },
      showCollapseButton: {
        type: Boolean,
        default: false,
      },
      isSubsection: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['expand', 'collapse'],
    computed: {
      truncatedDescription () {
        if (!this.community?.description) return ''
        const maxLength = 80
        if (this.community.description.length <= maxLength) {
          return this.community.description
        }
        return this.community.description.substring(0, maxLength).trim() + '...'
      },
    },
    mixins: [TranslationsMixin],
    components: {
      BasicHero,
    },
  }
</script>

<style lang="scss">

  .community-hero__back-button-wrapper {
    transition: margin 0.3s ease-in-out;

    .second-top-bar__link-item {
      transition: margin 0.3s ease-in-out;
      flex-shrink: 0;
    }

    @media (max-width: 576px) {
      padding: 0 8px;
    }
  }

  .community-hero__collapsed-info {
    display: flex;
    flex-direction: column;
    margin-left: 10px;
    animation: fadeIn 0.3s ease-in-out;
    overflow: hidden;
    min-width: 0; // Allow text truncation in flex container

    @media (max-width: 576px) {
      margin-left: 6px;
    }
  }

  .community-hero__collapsed-community-title {
    color: white;
    font-size: 1.2rem;
    margin-top: 0;
    margin-bottom: 0;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 768px) {
      font-size: 1rem;
    }

    @media (max-width: 576px) {
      font-size: 0.9rem;
    }
  }

  .community-hero__collapsed-description-row {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;

    @media (max-width: 576px) {
      display: none; // Hide description on very small screens when collapsed
    }
  }

  .community-hero__collapsed-description {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.85rem;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 500px;

    @media (max-width: 992px) {
      max-width: 350px;
    }

    @media (max-width: 768px) {
      max-width: 200px;
      font-size: 0.8rem;
    }
  }

  .community-hero__see-more,
  .community-hero__show-less {
    background: none;
    border: none;
    color: white;
    font-size: 0.85rem;
    padding: 0;
    margin-left: 4px;
    cursor: pointer;
    text-decoration: underline;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }

    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }

  .community-hero__show-less-container {
    padding: 0 10px 10px;
    text-align: left;

    @media (max-width: 576px) {
      padding: 0 8px 8px;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .button--minimal:hover .svg-icon path {
    fill: white !important;
  }

  .community-hero {
    background: var(--primary-community);
    z-index: 21;
    transition: all 0.3s ease-in-out;

    &.collapsed {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      box-shadow: 0 6px 5px -4px rgba(0, 0, 0, 0.5);

      .community-hero__back-button-wrapper {
        margin-top: 10px;

        @media (max-width: 576px) {
          margin-top: 8px;
        }
      }

      .basic-hero {
        padding-top: 0.5rem;
        padding-bottom: 0;

        .basic-hero__header {
          opacity: 0;
          max-height: 0;
          overflow: hidden;
          transform: translateY(-20px);
        }
      }
    }

    .container {
      margin: 0 auto;

      &.basic-hero__subtitle {
        margin: 0;
      }
    }

    .basic-hero {
      padding-top: 2.5rem;
      padding-bottom: 0;
      text-align: left;
      transition: padding 0.3s ease-in-out;

      @media (max-width: 768px) {
        padding-top: 1.5rem;
      }

      @media (max-width: 576px) {
        padding-top: 1rem;
      }

      .basic-hero__header {
        opacity: 1;
        max-height: 1000px;
        overflow: visible;
        transform: translateY(0);
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out, max-height 0.3s ease-in-out;
      }

      .basic-hero__slot {
        width: 100%;
        position: sticky;
        top: 70px; // Stick below topbar/secondtopbar
        display: block !important;
        background: var(--primary-community);
        z-index: 19;
        margin-top: 0;
        margin-left: -10px;
        margin-right: -10px;
        padding-left: 10px;
        padding-right: 10px;
        transition: all 0.3s ease-in-out;

        @media (max-width: 768px) {
          top: 56px; // Smaller topbar on mobile
        }

        .basic-hero-nav-bar {
          background: transparent;
          justify-content: flex-start;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;

          // Hide scrollbar but keep functionality
          scrollbar-width: none;
          -ms-overflow-style: none;

          &::-webkit-scrollbar {
            display: none;
          }

          .top-bar__links {
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;
            justify-content: flex-start;
            align-items: baseline;
            flex-wrap: nowrap;
            gap: 0;

            // Hide scrollbar but keep functionality
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
              display: none;
            }

            .button {
              padding: 5px 12px 0;
              flex-shrink: 0;

              @media (max-width: 768px) {
                padding: 4px 8px 0;
              }

              @media (max-width: 576px) {
                padding: 4px 6px 0;
              }
            }

            :deep(.button__label) {
              color: white;
              text-transform: uppercase;
              white-space: nowrap;

              @media (max-width: 768px) {
                font-size: 0.75rem;
              }

              @media (max-width: 576px) {
                font-size: 0.7rem;
              }
            }

            .button--minimal:focus {
              // override !important in focus-state.scss
              box-shadow: none !important;
            }

            .second-top-bar__link-active {
              background-color: var(--primary-community-light);
              color: black;
              font-weight: bold;
            }
          }
        }
      }
    }
  }

</style>
